import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconDownload, IconPlayerPause, IconPlayerPlay, IconRefresh } from '@tabler/icons-react';
import axios from '../../../library/axios';
import { getCpanelToken } from '../../../helpers/cpanelAuth';
import CpanelPageShell from '../../../components/cpanel/CpanelPageShell';
import './jobs.css';

const STATUS_LABELS = {
  pending: 'Menunggu',
  running: 'Berjalan',
  completed: 'Selesai',
  failed: 'Gagal',
  cancelled: 'Dihentikan',
};

const IMPORT_JOB_TYPES = new Set([
  'cpanel.user.import',
  'hospital.user.import',
  'tenant.import',
]);

const POLL_INTERVAL_MS = 1500;
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);
const ACTIVE_STATUSES = new Set(['pending', 'running']);

const CpanelJobPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(true);
  const [streamStatus, setStreamStatus] = useState('connecting');
  const [actionLoading, setActionLoading] = useState(null);
  const outputRef = useRef(null);
  const abortRef = useRef(null);
  const streamActiveRef = useRef(false);

  const scrollToBottom = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  };

  const applyJobSnapshot = useCallback((data) => {
    if (!data) return;
    setJob(data);
    if (data.output != null && data.output !== '') {
      setOutput(data.output);
      setTimeout(scrollToBottom, 0);
    }
  }, []);

  const fetchJob = useCallback(async () => {
    const { data } = await axios.get(`/cpanel/jobs/show/${id}`);
    applyJobSnapshot(data);
    return data;
  }, [id, applyJobSnapshot]);

  const startStream = useCallback(() => {
    if (streamActiveRef.current) return;

    const token = getCpanelToken();
    const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
    const controller = new AbortController();
    abortRef.current = controller;
    streamActiveRef.current = true;

    setStreamStatus('connecting');

    (async () => {
      try {
        const response = await fetch(`${base}/cpanel/jobs/stream/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error('Gagal membuka stream job');
        }

        setStreamStatus('streaming');
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const processChunk = (chunk) => {
          setOutput((prev) => prev + chunk);
          setTimeout(scrollToBottom, 0);
        };

        const parseEvents = () => {
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';

          parts.forEach((block) => {
            const lines = block.split('\n');
            let event = 'message';
            let dataLine = '';

            lines.forEach((line) => {
              if (line.startsWith('event:')) event = line.slice(6).trim();
              if (line.startsWith('data:')) dataLine += line.slice(5).trim();
            });

            if (!dataLine) return;

            try {
              const payload = JSON.parse(dataLine);
              if (event === 'output') {
                if (payload.full) {
                  setOutput(payload.chunk || '');
                  setTimeout(scrollToBottom, 0);
                } else {
                  processChunk(payload.chunk || '');
                }
              }
              if (event === 'status') {
                setJob((prev) =>
                  prev
                    ? {
                        ...prev,
                        status: payload.status,
                        exitCode: payload.exitCode,
                        errorMessage: payload.error,
                      }
                    : prev
                );
                setStreamStatus(payload.status);
              }
              if (event === 'done') {
                setStreamStatus(payload.status || 'completed');
                fetchJob().catch(() => {});
              }
            } catch {
              // ignore malformed SSE chunk
            }
          });
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          parseEvents();
        }

        parseEvents();
        setStreamStatus((current) => (current === 'streaming' ? 'closed' : current));
      } catch (err) {
        if (err.name !== 'AbortError') {
          setStreamStatus('error');
        }
      } finally {
        streamActiveRef.current = false;
      }
    })();
  }, [id, fetchJob]);

  useEffect(() => {
    let active = true;
    let pollTimer = null;

    const init = async () => {
      try {
        const data = await Promise.race([
          fetchJob(),
          new Promise((_, reject) => {
            window.setTimeout(() => reject(new Error('timeout')), 20000);
          }),
        ]);
        if (!active) return;

        setLoading(false);

        if (!TERMINAL_STATUSES.has(data.status)) {
          startStream();
          pollTimer = window.setInterval(async () => {
            try {
              const fresh = await fetchJob();
              if (!active) return;
              if (TERMINAL_STATUSES.has(fresh.status)) {
                setStreamStatus(fresh.status);
                window.clearInterval(pollTimer);
                pollTimer = null;
              }
            } catch {
              //
            }
          }, POLL_INTERVAL_MS);
        } else {
          setStreamStatus(data.status);
        }
      } catch {
        if (active) {
          setStreamStatus('error');
          setLoading(false);
          try {
            await fetchJob();
          } catch {
            // ignore secondary fetch failure
          }
        }
      }
    };

    init();

    return () => {
      active = false;
      if (pollTimer) window.clearInterval(pollTimer);
      abortRef.current?.abort();
      streamActiveRef.current = false;
    };
  }, [id, fetchJob, startStream]);

  const isImportJob = job && IMPORT_JOB_TYPES.has(job.type);
  const canStop = job && ACTIVE_STATUSES.has(job.status);
  const canResume =
    job?.canResume === true ||
    (job?.canResume == null &&
      job &&
      (job.status === 'cancelled' || job.status === 'failed') &&
      isImportJob &&
      job.import &&
      Number(job.import.processedRows) < Number(job.import.totalRows));
  const canRestart =
    job &&
    TERMINAL_STATUSES.has(job.status) &&
    job.metadata?.replayPayload &&
    !canResume;

  const runJobAction = async (action, endpoint) => {
    if (actionLoading) return;
    setActionLoading(action);
    try {
      const { data } = await axios.post(endpoint);
      if (action === 'restart' || action === 'resume') {
        const nextId = data?.jobId;
        if (nextId && nextId !== id) {
          navigate(`/cpanel/jobs/${nextId}`, { replace: true });
          return;
        }
      }
      abortRef.current?.abort();
      streamActiveRef.current = false;
      const fresh = await fetchJob();
      if (!TERMINAL_STATUSES.has(fresh?.status)) {
        startStream();
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Aksi job gagal.';
      window.alert(message);
    } finally {
      setActionLoading(null);
    }
  };

  const statusBadgeClass =
    job?.status === 'completed'
      ? 'success'
      : job?.status === 'failed'
        ? 'danger'
        : job?.status === 'cancelled'
          ? 'warning'
          : job?.status === 'running'
            ? 'primary'
            : 'secondary';

  const handleDownloadExport = async () => {
    if (!job?.exportId) return;
    try {
      const response = await axios.get(`/cpanel/exports/download/${job.exportId}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = job.exportFileName || job.metadata?.downloadName || 'export';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      window.alert('Gagal mengunduh file ekspor.');
    }
  };

  return (
    <CpanelPageShell
      title="Job Runner"
      subtitle="Streaming output exec"
      breadcrumbs={[
        { label: 'CPanel', to: '/cpanel/dashboard' },
        { label: 'Log', to: '/cpanel/logs' },
        { label: 'Job Runner' },
      ]}
    >
      <div className="card cpanel-card">
        <div className="card-header border-0 pt-6 d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <h3 className="card-title fw-bold mb-1">Job {id}</h3>
            {job ? (
              <div className="d-flex flex-wrap gap-2 align-items-center text-muted fs-7">
                <span className={`badge badge-light-${statusBadgeClass}`}>
                  {STATUS_LABELS[job.status] || job.status}
                </span>
                <span>{job.type}</span>
                {job.import ? (
                  <span>
                    Import: {job.import.processedRows}/{job.import.totalRows} baris
                  </span>
                ) : null}
                {job.command ? <span className="font-monospace">{job.command}</span> : null}
              </div>
            ) : null}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {canStop ? (
              <button
                type="button"
                className="btn btn-sm btn-warning"
                disabled={!!actionLoading}
                onClick={() => runJobAction('stop', `/cpanel/jobs/${id}/stop`)}
              >
                <IconPlayerPause size={16} stroke={2} className="me-1" />
                {actionLoading === 'stop' ? 'Menghentikan...' : 'Stop'}
              </button>
            ) : null}
            {canResume ? (
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={!!actionLoading}
                onClick={() => runJobAction('resume', `/cpanel/jobs/${id}/resume`)}
              >
                <IconPlayerPlay size={16} stroke={2} className="me-1" />
                {actionLoading === 'resume' ? 'Melanjutkan...' : 'Resume'}
              </button>
            ) : null}
            {canRestart ? (
              <button
                type="button"
                className="btn btn-sm btn-info"
                disabled={!!actionLoading}
                onClick={() => runJobAction('restart', `/cpanel/jobs/${id}/restart`)}
              >
                <IconRefresh size={16} stroke={2} className="me-1" />
                {actionLoading === 'restart' ? 'Memulai ulang...' : 'Restart'}
              </button>
            ) : null}
            {job?.status === 'completed' && job?.exportId ? (
              <button type="button" className="btn btn-sm btn-success" onClick={handleDownloadExport}>
                <IconDownload size={16} stroke={2} className="me-1" />
                Unduh hasil ekspor
              </button>
            ) : null}
            <Link to="/cpanel/logs" className="btn btn-sm btn-light">
              Kembali ke Log
            </Link>
          </div>
        </div>
        <div className="card-body pt-0">
          {loading ? (
            <div className="text-muted py-5">Memuat job...</div>
          ) : (
            <>
              {job?.status === 'failed' && job?.errorMessage ? (
                <div className="alert alert-danger py-3 px-4 mb-4" role="alert">
                  <strong>Job gagal:</strong> {job.errorMessage}
                </div>
              ) : null}
              <div className="cpanel-job-stream-meta mb-3 text-muted fs-7">
                Stream: {streamStatus}
                {job?.exitCode != null ? ` · exit ${job.exitCode}` : ''}
                {job?.errorMessage && job?.status !== 'failed' && job?.status !== 'cancelled'
                  ? ` · ${job.errorMessage}`
                  : ''}
              </div>
              <pre ref={outputRef} className="cpanel-job-output mb-0">
                {output || '(belum ada output)'}
              </pre>
            </>
          )}
        </div>
      </div>
    </CpanelPageShell>
  );
};

export default CpanelJobPage;
